import { create } from "zustand";
import type { AnalysisPreset } from "./analysis-presets";
import type { AnalysisProgress, ComputedGameAnalysis } from "./game-analyzer";
import { hasFullEngineDownload, PRESETS } from "./analysis-presets";

export type AnalysisRunSource = "batch" | "single";

export interface AnalysisJob {
  gameId: string;
  opponent: string;
  status: "queued" | "analyzing" | "done" | "error";
  progress: AnalysisProgress | null;
  result: ComputedGameAnalysis | null;
  error: string | null;
  preset: AnalysisPreset;
  resultLabel: "saved" | "kept-stronger" | null;
  startedAt: number | null;
  durationMs: number | null;
  attempts: number;
}

interface AnalysisState {
  /** Currently selected preset */
  preset: AnalysisPreset;
  /** Queue of games being analyzed */
  jobs: AnalysisJob[];
  /** High-frequency move progress lives outside jobs so it does not replace
   * the entire queue (and Virtuoso data) for every analyzed position. */
  progressByGameId: Record<string, AnalysisProgress | null>;
  /** Whether the engine is currently running */
  isRunning: boolean;
  runSource: AnalysisRunSource | null;
  /** Overall progress (games completed / total) */
  gamesCompleted: number;
  gamesTotal: number;
  /** Engine init/download state — lifted out of AnalysisPage's local state so it survives navigating away and back while a batch is still starting up. */
  initError: string | null;
  downloadPercent: number | null;
  enginePhase: string | null;
  fullEngineDownloaded: boolean;

  setPreset: (preset: AnalysisPreset) => void;
  addJobs: (jobs: Pick<AnalysisJob, "gameId" | "opponent" | "preset">[]) => void;
  updateJob: (gameId: string, update: Partial<AnalysisJob>) => void;
  setRunning: (running: boolean) => void;
  incrementCompleted: () => void;
  clearSession: () => void;
  reset: (source: AnalysisRunSource) => void;
  setInitError: (error: string | null) => void;
  setDownloadPercent: (percent: number | null) => void;
  setEnginePhase: (phase: string | null) => void;
  setFullEngineDownloaded: (downloaded: boolean) => void;
}

export const useAnalysisStore = create<AnalysisState>((set) => ({
  preset: PRESETS[1], // Default: Balanced
  jobs: [],
  progressByGameId: {},
  isRunning: false,
  runSource: null,
  gamesCompleted: 0,
  gamesTotal: 0,
  initError: null,
  downloadPercent: null,
  enginePhase: null,
  fullEngineDownloaded: hasFullEngineDownload(),

  setPreset: (preset) => set({ preset }),

  addJobs: (newJobs) =>
    set((state) => ({
      jobs: [
        ...state.jobs,
        ...newJobs.map((j) => ({
          gameId: j.gameId,
          opponent: j.opponent,
          status: "queued" as const,
          progress: null,
          result: null,
          error: null,
          preset: j.preset,
          resultLabel: null,
          startedAt: null,
          durationMs: null,
          attempts: 0,
        })),
      ],
      progressByGameId: {
        ...state.progressByGameId,
        ...Object.fromEntries(newJobs.map((job) => [job.gameId, null])),
      },
      gamesTotal: state.gamesTotal + newJobs.length,
    })),

  updateJob: (gameId, update) =>
    set((state) => {
      const { progress, ...jobUpdate } = update;
      if (progress !== undefined && Object.keys(jobUpdate).length === 0) {
        // Mutating this internal lookup is intentional: Zustand selectors for
        // other game IDs keep the same value reference and do not rerender.
        state.progressByGameId[gameId] = progress;
        return { progressByGameId: state.progressByGameId };
      }
      if (progress !== undefined) state.progressByGameId[gameId] = progress;
      return {
        jobs: state.jobs.map((job) => (job.gameId === gameId ? { ...job, ...jobUpdate, progress: job.progress } : job)),
        progressByGameId: state.progressByGameId,
      };
    }),

  setRunning: (running) => set({ isRunning: running }),
  incrementCompleted: () => set((state) => ({ gamesCompleted: state.gamesCompleted + 1 })),
  clearSession: () =>
    set({
      jobs: [],
      progressByGameId: {},
      gamesCompleted: 0,
      gamesTotal: 0,
      initError: null,
      downloadPercent: null,
      enginePhase: null,
      runSource: null,
    }),

  reset: (source) =>
    set({
      jobs: [],
      progressByGameId: {},
      isRunning: false,
      runSource: source,
      gamesCompleted: 0,
      gamesTotal: 0,
      initError: null,
      downloadPercent: null,
      enginePhase: null,
    }),

  setInitError: (initError) => set({ initError }),
  setDownloadPercent: (downloadPercent) => set({ downloadPercent }),
  setEnginePhase: (enginePhase) => set({ enginePhase }),
  setFullEngineDownloaded: (fullEngineDownloaded) => set({ fullEngineDownloaded }),
}));
